// ============================================================
// CRITICAL SEVERITY, AND WHERE THE TABLES TURN FATAL.
//
// Separate from `protagonist.ts` because it reads the critical tables,
// and `protagonist.ts` must stay dependency-free: `types.ts` imports it,
// and `criticalTables.ts` imports `types.ts`.
//
// THE TABLES ARE NOT MONOTONIC. Impact body is fatal at severity 7 and
// survivable at 8. So "the first fatal severity" is not a threshold a GM
// can reason with, and stepping DOWN is the only safe direction.
// ============================================================
import { getCritForBody } from '../criticalTables';
import type { BodyTypeId } from '../../types';

/** Damage types the humanoid tables are keyed by. */
const DAMAGE_TYPES = ['I', 'E', 'R', 'X'] as const;
/** The four table columns - left and right collapse before lookup. */
const LOCATIONS = ['head', 'body', 'arm', 'leg'] as const;

/**
 * Does this effect string end the character?
 *
 * Covers the outright kill, both shapes of dying (the untreated `Dy` that
 * never ticks and the clocked `DyN` that does), and a vehicle exploding.
 *
 * `Explodes` is checked in its own right rather than trusting the `K`
 * that currently accompanies every such row: `useApplyHits` matches the
 * `'K'` token exactly, so one future `Explodes`-without-`K` entry would
 * slip past it. Cheap to be right about now.
 */
export function isDeadlyFx(fx: string): boolean {
  return (fx || '')
    .split(',')
    .map((c) => c.trim())
    .some((c) => c === 'K' || c === 'Explodes' || c === 'Dy' || /^Dy./.test(c));
}

/**
 * The highest severity at or below `severity` whose result is survivable,
 * or null if there is none.
 *
 * Downward, never upward: an Impact body hit capped at 7 must not be
 * "fixed" by promoting it to the survivable 8.
 */
export function survivableSeverity(
  damageType: string,
  locationId: string,
  severity: number,
  bodyType: BodyTypeId = 'humanoid',
): number | null {
  for (let s = Math.min(severity, 10); s >= 1; s--) {
    if (!isDeadlyFx(getCritForBody(damageType, locationId, s, bodyType).fx)) return s;
  }
  return null;
}

/**
 * Every severity at or below `cap` that can still kill, on any humanoid
 * table - what the editor tells a GM about the cap they just typed.
 *
 * Without this the number is a guess, and the obvious guesses are wrong:
 * 7 feels safe and is not (Impact body), and the lowest fatal row
 * anywhere is Explosive body at 5.
 */
export function deadlySeveritiesUpTo(cap: number): number[] {
  const found: number[] = [];
  for (let s = 1; s <= Math.min(cap, 10); s++) {
    const deadly = DAMAGE_TYPES.some((dt) =>
      LOCATIONS.some((loc) => isDeadlyFx(getCritForBody(dt, loc, s, 'humanoid').fx)),
    );
    if (deadly) found.push(s);
  }
  return found;
}

/**
 * The highest cap at which no humanoid table can produce a death.
 *
 * Derived rather than written down, so it cannot drift away from the
 * tables - `critCap.test.ts` asserts both halves of the boundary.
 */
export const SAFE_SEVERITY_CAP: number = (() => {
  for (let cap = 10; cap >= 1; cap--) {
    if (deadlySeveritiesUpTo(cap).length === 0) return cap;
  }
  return 0;
})();
